'use client'

import * as React from 'react'
import Script from 'next/script'
import type { User } from '@repo/cf-user/hooks/use-user-data'
import { useUser } from '@repo/cf-user/hooks/use-user-data'
import { isLoggedInSelector } from 'auth/selectors'
import { useUserCheckEarlyTrialCancelQuery } from 'codegen/generated/user'

type UserType = 'visitor' | 'user' | 'subscriber' | 'customer' | 'designer'

type ZendeskUserType = 'visitor' | 'user' | 'premium_user' | 'designer'

export type TicketGenieChannelName = 'user' | 'enterprise'

const mapCustomerTypeToZendeskUserType = new Map<UserType, ZendeskUserType>([
  ['visitor', 'visitor'],
  ['user', 'user'],
  ['customer', 'user'],
  ['subscriber', 'premium_user'],
  ['designer', 'designer'],
])

const mapChannelNameToChannelId = new Map<TicketGenieChannelName, string | unknown>([
  ['user', process.env.NEXT_PUBLIC_TICKET_GENIE_CHANNEL_ID],
  ['enterprise', process.env.NEXT_PUBLIC_TICKET_GENIE_ENTERPRISE_CHANNEL_ID],
])

export const decideCustomerType = (user: User | undefined): UserType => {
  const isLoggedIn = isLoggedInSelector({ user })

  if (!user || !isLoggedIn) {
    return 'visitor'
  }

  //TODO - uncomment and refactor when GQL returns the data
  /*if (user.identification.isDesigner) {
    return 'designer'
  }*/

  if (user.subscriptionAccess.allAccess.isSubscribed) {
    return 'subscriber'
  }

  //TODO - uncomment and refactor when GQL returns the data
  /*if (user.isCustomer) {
    return 'customer'
  }*/

  return 'user'
}

// oxlint-disable-next-line complexity
export const TicketGenieSnippet = ({
  locale,
  channel = 'user',
}: {
  locale: string
  channel?: TicketGenieChannelName
}) => {
  const user = useUser()
  const isLoggedIn = isLoggedInSelector({ user: user.data })

  const activeSubscriptions = []
  if (user.data?.subscriptionAccess.allAccess.isSubscribed) {
    activeSubscriptions.push('allAccess')
  }
  if (user.data?.subscriptionAccess.studioAi.isSubscribed) {
    activeSubscriptions.push('studioAi')
  }

  const shouldFetchEarlyTrial =
    isLoggedIn && !!user.data?.stripeCustomerId && activeSubscriptions.length === 1
  const { data: earlyTrialData, isPending } = useUserCheckEarlyTrialCancelQuery(
    { stripeCustomerId: user.data?.stripeCustomerId || '' },
    {
      enabled: shouldFetchEarlyTrial,
    },
  )

  const customerType = decideCustomerType(user.data || undefined)

  const userDataSnippet =
    isLoggedIn && user.data
      ? `userData: () => {
            return {
              user: {
                email: "${user.data?.email}",
                displayName: "${user.data?.firstName ? `${user.data.firstName} ` : ''}${
                  user.data?.lastName ? user.data.lastName : ''
                }",
              },
              metadata: {
                cf_customer_id: "${user.data?.id}",
                customer_type: "${customerType}",
                stripe_customer_id: '${user.data?.stripeCustomerId || ''}',
                early_trial_cancel: '${earlyTrialData?.userCheckEarlyTrialCancel?.earlyTrialCancel ? 'true' : 'false'}',
              },
            };
          },`
      : ''

  React.useEffect(() => {
    const handleResize = () => {
      // Allow overriding of the widget position
      if (document.body.style.getPropertyValue('--widget-bottom') !== '') {
        return
      }

      // oxlint-disable-next-line unicorn/prefer-query-selector
      const banner = document.getElementsByClassName('fixed-banner')
      // oxlint-disable-next-line unicorn/prefer-query-selector
      const productStickyInfo = document.getElementById('sticky-info')
      // oxlint-disable-next-line unicorn/prefer-query-selector
      const promptBarBanner = document.getElementById('prompt-bar-banner')

      if (banner.length > 0 || productStickyInfo || promptBarBanner) {
        // oxlint-disable-next-line unicorn/prefer-query-selector
        const supportButton = document.getElementById('tg-support-widget-tgsw')

        if (supportButton) {
          if (window.innerWidth < 1050 && promptBarBanner) {
            const rect = promptBarBanner.getBoundingClientRect()
            supportButton.style.setProperty('--widget-bottom', `${rect.height - 20}px`)

            return
          }

          if (window.innerWidth < 768 && banner[0]) {
            const rect = banner[0].getBoundingClientRect()
            supportButton.style.setProperty('--widget-bottom', `${rect.height + 5}px`)

            return
          }

          if (window.innerWidth < 960 && productStickyInfo) {
            const rect = productStickyInfo.getBoundingClientRect()
            supportButton.style.setProperty('--widget-bottom', `${rect.height + 5}px`)

            return
          }

          supportButton.style.setProperty('--widget-bottom', '20px')
        }
      }
    }

    const handleScroll = () => {
      setTimeout(() => {
        // Allow overriding of the widget position
        if (document.body.style.getPropertyValue('--widget-bottom') !== '') {
          return
        }

        // oxlint-disable-next-line unicorn/prefer-query-selector
        const banner = document.getElementsByClassName('fixed-banner')
        // oxlint-disable-next-line unicorn/prefer-query-selector
        const scrollToTopButton = document.getElementById('rocket-button')
        // oxlint-disable-next-line unicorn/prefer-query-selector
        const supportButton = document.getElementById('tg-support-widget-tgsw')
        // oxlint-disable-next-line unicorn/prefer-query-selector
        const productStickyInfo = document.getElementById('sticky-info')
        // oxlint-disable-next-line unicorn/prefer-query-selector
        const promptBarBanner = document.getElementById('prompt-bar-banner')

        if (supportButton) {
          if (window.innerWidth < 1050 && promptBarBanner) {
            const rect = promptBarBanner.getBoundingClientRect()
            supportButton.style.setProperty('--widget-bottom', `${rect.height + 5}px`)

            return
          }

          if (banner.length > 0) {
            const rect = banner[0].getBoundingClientRect()
            supportButton.style.setProperty('--widget-bottom', `${rect.height + 5}px`)
            const supportButtonHeight =
              // oxlint-disable-next-line unicorn/prefer-query-selector
              document.getElementById('widget-button-tgsw')?.getBoundingClientRect().height || 0

            scrollToTopButton &&
              scrollToTopButton.style.setProperty(
                'bottom',
                `${supportButtonHeight + rect.height + 20}px`,
              )

            return
          }

          if (window.innerWidth < 960 && productStickyInfo) {
            const rect = productStickyInfo.getBoundingClientRect()
            supportButton.style.setProperty('--widget-bottom', `${rect.height + 5}px`)

            return
          }

          supportButton.style.setProperty('--widget-bottom', '20px')
        }
      }, 300)
    }

    window.addEventListener('resize', handleResize)
    window.addEventListener('scroll', handleScroll)
    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  if (user.isPending || (shouldFetchEarlyTrial && isPending)) {
    return null
  }

  return (
    <Script id="ticket-genie-script" strategy="afterInteractive">
      {`
         (function (w, d, s, o, f, js, fjs) {
           w.CF = w.CF || {};
           w.CF[o] =
             w.CF[o] ||
             function () {
               (w.CF[o].q = w.CF[o].q || []).push(arguments);
             };
           (js = d.createElement(s)), (fjs = d.getElementsByTagName(s)[0]);
           js.id = o;
           js.src = f;
           js.async = 1;
           fjs.parentNode.insertBefore(js, fjs);
         })(window, document, 'script', 'tgsw', '${process.env.NEXT_PUBLIC_TICKET_GENIE_URL}');
         CF.tgsw('init', {
           channelId: '${mapChannelNameToChannelId.get(channel)}',
           // oxlint-disable-next-line no-negated-condition
           customerName: '${process.env.NEXT_PUBLIC_ENV !== 'staging' ? 'creativefabrica' : 'cf'}',
           lang: '${locale}',
           zendeskCustomFields: () => {
             return [
               {
                 fieldId: 4807001644316,
                 value: '${mapCustomerTypeToZendeskUserType.get(customerType)}',
               },
             ];
           },
           ${userDataSnippet}
         });
        `}
    </Script>
  )
}
